import React from "react";
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  InfoTooltip,
} from "@tiny/components";
import { TypographyProps } from "@tiny/components/src/Typography/Typography.types";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import Info2Outline from "@tiny/icons/generated/Info2Outline";
import CloseBig from "@tiny/icons/generated/CloseBig";
import {
  formatNumber,
  formatPercent,
} from "@tiny/components/src/utils/formatUtils";
import { useAudienceSummary } from "./AudienceSummary.hooks";
import { useStyles } from "./AudienceSummary.style";

export interface AudienceSummaryProps {
  segmentIds: string[];
  noBorderRadius?: boolean;
  onClose?: () => void;
}

function AudienceSummary(props: AudienceSummaryProps) {
  const classes = useStyles();
  const {
    subscribedCount,
    suppressedCount,
    clickedPercentage,
    openedPercentage,
    noBorderRadius,
    onClose,
    isps,
    isLoading,
    includeGlobalSuppressed,
    includeLocalSuppressed,
    handleIncludeGlobalSuppressed,
    calculateSuprressionPercentage,
  } = useAudienceSummary(props);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className={classes.dialogContainer}
      borderRadius={noBorderRadius ? "0px" : "8px"}
      component="section"
    >
      <Title>Audience Summary</Title>
      <Box className={classes.absoluteTopRight}>
        {onClose ? (
          <IconButton size="small" onClick={onClose} color="inherit">
            <CloseBig />
          </IconButton>
        ) : (
          <Tooltip title="Total number of subscribers from the audience who qualify based on the criteria.">
            <IconButton size="tiny" color="primary">
              <Info2Outline />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {includeGlobalSuppressed || includeLocalSuppressed ? (
        <>
          <Body>{formatNumber(subscribedCount)} Subscribers</Body>
          <Body>{formatNumber(suppressedCount)} Suppressed</Body>
          <Body>
            {formatNumber(subscribedCount - suppressedCount)} Subscribers after
            Suppression
          </Body>
        </>
      ) : (
        <Body>{formatNumber(subscribedCount)} Subscribers</Body>
      )}
      <Subtitle mb={2}>Your breakdown across selected audiences</Subtitle>
      <LinearProgress variant="determinate" value={openedPercentage} />
      <Body mt={1} mb={2}>
        {formatPercent(openedPercentage)} have opened before
      </Body>
      <LinearProgress variant="determinate" value={clickedPercentage} />
      <Body mt={1}>{formatPercent(clickedPercentage)} have clicked before</Body>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Subtitle>ISP Breakdown</Subtitle>
        {!includeGlobalSuppressed && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleIncludeGlobalSuppressed}
          >
            Check Suppression
          </Button>
        )}
      </Box>
      <Box className={classes.sectionContent}>
        {isps?.map((isp) => {
          const subscribedCount = isp?.subscribedCount ?? 0;
          const globalSuppressed = isp?.globalSuppressed ?? 0;
          const localSuppressed = isp?.localSuppressed ?? 0;

          const {
            subscribedPercent,
            globalPercent,
            localPercent,
            remainSubscription,
          } = calculateSuprressionPercentage(
            subscribedCount,
            includeGlobalSuppressed ? globalSuppressed : 0,
            includeLocalSuppressed ? localSuppressed : 0
          );

          return (
            <Box key={isp?.ispName} className={classes.dialogContent}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Body>{isp?.ispName}</Body>
                {includeGlobalSuppressed || includeLocalSuppressed ? (
                  <Body>{remainSubscription.toLocaleString()}</Body>
                ) : (
                  <Body>{subscribedCount.toLocaleString()}</Body>
                )}
              </Box>
              <Box className={classes.progressBarContainer}>
                {remainSubscription > 0 && (
                  <Tooltip
                    title={
                      <>
                        <div>
                          Opens: {isp?.uniqueOpenCount?.toLocaleString() || 0}
                        </div>
                        <div>
                          Clicks: {isp?.uniqueClickCount?.toLocaleString() || 0}
                        </div>
                        <hr />
                        <div>
                          Total Subscribers:{" "}
                          {subscribedCount.toLocaleString() || 0}
                        </div>
                        {(includeGlobalSuppressed ||
                          includeLocalSuppressed) && (
                          <Box>
                            <div>
                              Suppressed:{" "}
                              {(
                                (isp?.globalSuppressed || 0) +
                                (isp?.localSuppressed || 0)
                              ).toLocaleString()}
                            </div>
                            <div>
                              Total Subscribers after Suppression:{" "}
                              {remainSubscription.toLocaleString() || 0}
                            </div>
                          </Box>
                        )}
                      </>
                    }
                    arrow
                    customContent
                  >
                    <Box
                      flex={subscribedPercent}
                      className={classes.progressBarSegmentPrimary}
                    />
                  </Tooltip>
                )}

                {includeGlobalSuppressed && globalSuppressed > 0 && (
                  <Tooltip
                    title={
                      <>
                        <div>
                          Global Suppressed:{" "}
                          {globalSuppressed?.toLocaleString() || 0}
                        </div>
                      </>
                    }
                    arrow
                    customContent
                  >
                    <Box
                      flex={globalPercent}
                      className={classes.progressBarSegmentRed}
                    />
                  </Tooltip>
                )}

                {includeLocalSuppressed && localSuppressed > 0 && (
                  <Tooltip
                    title={`Local Suppressed: ${localSuppressed.toLocaleString()}`}
                    arrow
                    customContent
                  >
                    <Box
                      flex={localPercent}
                      className={classes.progressBarSegmentYellow}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function Title(props: Partial<TypographyProps>) {
  const { children, ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "h4" : "h3"}
      color="grey"
      tone={900}
      fontWeight="semiBold"
      m={0}
    >
      {children}
    </Typography>
  );
}

function Subtitle(props: Partial<TypographyProps>) {
  const { children, ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "label2" : "label1"}
      color="grey"
      tone={900}
      fontWeight="bold"
      display="block"
      mt={4}
    >
      {children}
    </Typography>
  );
}

function Body(props: Partial<TypographyProps>) {
  const { children, wordBreak = "break-all", ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "body3" : "body2"}
      color="grey"
      tone={700}
      display="block"
      wordBreak={wordBreak}
    >
      {children}
    </Typography>
  );
}

export default React.memo(AudienceSummary);
